import React, { useEffect, useState } from "react";
import { useApi } from "../apiHelpers";
import { useToast } from "../components/Toast/ToastContext";
import { useConfirm } from "../components/ConfirmModal/ConfirmContext";
import { buttonClass } from "../components/ui/Button";
import { Checkbox } from "../components/ui/Checkbox";
import { Field, FieldRow } from "../components/ui/Field";
import Select from "../components/ui/Select";
import Tag from "../components/ui/Tag";
import Tooltip from "../components/ui/Tooltip";
import Alert from "../components/ui/Alert";
import Reveal from "../components/ui/Reveal";
import Appear from "../components/ui/Appear";
import { ListRow, ListStack } from "../components/ui/ListRow";
import { CAPABILITIES, ROLES } from "./userCapabilities";

/**
 * Invitations panel for User Management.
 *
 * Owns the whole invite lifecycle so UserAdmin needs one insertion point: the
 * Invite button, the form, and the list of outstanding invitations with resend
 * and revoke.
 *
 * Capabilities are chosen here rather than after the fact. The old flow was
 * "register yourself, then wait for an admin to find you and grant access"; the
 * invite already knows who the person is, so it may as well know what they do.
 */

type Invite = {
  id: string;
  email: string;
  firstname: string | null;
  lastname: string | null;
  capabilities: Record<string, boolean> | null;
  primary_role: string;
  isadmin: boolean;
  expires_at: string;
  sent_at: string | null;
  status: "pending" | "expired";
  invited_by_firstname: string | null;
  invited_by_lastname: string | null;
};

const emptyForm = {
  email: "",
  firstname: "",
  lastname: "",
  primary_role: "staff",
  isadmin: false,
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const UserInvites = () => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();

  const [invites, setInvites] = useState<Invite[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [capabilities, setCapabilities] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  // Only populated when the API could not send the email (no mailbox configured
  // on this deployment). The admin passes the link along by hand instead of
  // waiting on a message that will never arrive.
  const [manualLink, setManualLink] = useState<string | null>(null);

  const fetchInvites = async () => {
    try {
      setInvites((await api.get("/users/invites")) || []);
    } catch {
      // A failed invite fetch must not take the user list down with it.
      toast.error("Could not load pending invitations");
    }
  };

  useEffect(() => {
    fetchInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setCapabilities({});
    setShowForm(false);
  };

  const reportDelivery = (res: any, address: string) => {
    if (res.emailSent) {
      toast.success(`Invitation sent to ${address}`);
      setManualLink(null);
    } else {
      // Deliberately not phrased as a success. The invite exists, but nobody
      // has been told about it yet, and that difference matters.
      toast.error("Invitation created, but the email could not be sent. Share the link below.");
      setManualLink(res.inviteUrl || null);
    }
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/users/invites", {
        body: { ...form, ...capabilities },
      });
      reportDelivery(res, form.email.trim().toLowerCase());
      resetForm();
      fetchInvites();
    } catch (error: any) {
      toast.error(error?.data?.error?.message || "Could not create the invitation");
    } finally {
      setBusy(false);
    }
  };

  const resend = async (invite: Invite) => {
    setBusy(true);
    try {
      reportDelivery(await api.post(`/users/invites/${invite.id}/resend`), invite.email);
      fetchInvites();
    } catch (error: any) {
      toast.error(error?.data?.error?.message || "Could not resend the invitation");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (invite: Invite) => {
    if (!(await confirm({
      message: `Cancel the invitation for ${invite.email}?\n\nTheir link stops working immediately.`,
      confirmLabel: "Cancel invitation",
      cancelLabel: "Keep invitation",
      tone: "danger",
    }))) return;
    setBusy(true);
    try {
      await api.delete(`/users/invites/${invite.id}`);
      toast.success("Invitation cancelled");
      setManualLink(null);
      fetchInvites();
    } catch (error: any) {
      toast.error(error?.data?.error?.message || "Could not cancel the invitation");
    } finally {
      setBusy(false);
    }
  };

  const capsOn = (invite: Invite) =>
    CAPABILITIES.filter((cap: any) => invite.capabilities?.[cap.key]);

  return (
    <div className="mb-6 p-4 bg-surface-container-low rounded-lg">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-white">Invitations</h2>
          <p className="text-xs text-on-surface-variant mt-0.5">
            Accounts are created by invitation. The link is single use and expires on its own.
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className={buttonClass('filled')}
        >
          {showForm ? "Cancel" : "Invite User"}
        </button>
      </div>

      <Reveal show={showForm}>
        <form onSubmit={sendInvite} className="mt-4 space-y-3 pt-4">
          <FieldRow
            label="Email"
            hint="The account is created with this address. They cannot change it while accepting."
          >
            <Field
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="name@fireflydroneshows.com"
              required
            />
          </FieldRow>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="First Name">
              <Field
                type="text"
                value={form.firstname}
                onChange={(e) => setForm({ ...form, firstname: e.target.value })}
              />
            </FieldRow>
            <FieldRow label="Last Name">
              <Field
                type="text"
                value={form.lastname}
                onChange={(e) => setForm({ ...form, lastname: e.target.value })}
              />
            </FieldRow>
          </div>

          <div>
            <label className="text-xs text-on-surface-variant mb-2 block">Capabilities</label>
            <div className="flex flex-wrap gap-3">
              {CAPABILITIES.map((cap: any) => (
                <Checkbox
                  key={cap.key}
                  checked={capabilities[cap.key] || false}
                  onChange={(e) =>
                    setCapabilities((prev) => ({ ...prev, [cap.key]: e.target.checked }))
                  }
                  label={<span className={`text-sm ${cap.color}`}>{cap.label}</span>}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Default Home Screen">
              <Select
                label="Default Home Screen"
                value={form.primary_role}
                onChange={(key) => setForm({ ...form, primary_role: key })}
                options={ROLES.map((r: any) => ({ key: r.value, label: r.label }))}
              />
            </FieldRow>
            <div className="flex items-end">
              <Checkbox
                checked={form.isadmin}
                onChange={(e) => setForm({ ...form, isadmin: e.target.checked })}
                label={<span className="text-white">Legacy Admin</span>}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            className={buttonClass('filled')}
          >
            {busy ? "Sending..." : "Send Invitation"}
          </button>
        </form>
      </Reveal>

      <Alert show={!!manualLink} tone="warning" live={false} className="mt-4">
          <p className="mb-2 text-xs">
            Email is not configured on this deployment. Send this link to the invited person yourself.
          </p>
          <div className="flex gap-2 items-center">
            <code className="flex-1 text-xs text-on-surface bg-gray-900 px-2 py-1.5 rounded break-all">
              {manualLink}
            </code>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(manualLink ?? "");
                toast.success("Link copied");
              }}
              className={`${buttonClass('quiet', 'sm')} shrink-0`}
            >
              Copy
            </button>
          </div>
      </Alert>

      <Reveal show={invites.length > 0}>
        <ListStack
          className="mt-6"
          spacing="loose"
          items={invites}
          getKey={(invite: any) => String(invite.id)}
          renderRow={(invite: any) => (
            <ListRow
              actions={(
                <div className="flex gap-2 shrink-0">
                  <Tooltip label="Issue a new link and email it. Any earlier link stops working.">
                    <button
                      onClick={() => resend(invite)}
                      disabled={busy}
                      className={buttonClass('quiet', 'sm')}
                    >
                      Resend
                    </button>
                  </Tooltip>
                  <button
                    onClick={() => revoke(invite)}
                    disabled={busy}
                    className={buttonClass('danger', 'sm')}
                  >
                    Cancel
                  </button>
                </div>
              )}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white text-sm font-medium break-all">{invite.email}</span>
                  {invite.status === "expired" ? (
                    <Tag tone="bad">Expired</Tag>
                  ) : (
                    <Tag tone="info">Pending</Tag>
                  )}
                  <Appear show={!invite.sent_at}>
                    <Tooltip label="The invite exists but no email went out. Resend, or share the link by hand.">
                      <Tag tone="warn">Not emailed</Tag>
                    </Tooltip>
                  </Appear>
                </div>
                <div className="text-xs text-on-surface-variant mt-1">
                  {invite.firstname ? `${invite.firstname} ${invite.lastname || ""} · ` : ""}
                  {invite.status === "expired" ? "Expired" : "Expires"} {fmtDate(invite.expires_at)}
                  {invite.invited_by_firstname ? ` · Invited by ${invite.invited_by_firstname}` : ""}
                </div>
                {capsOn(invite).length > 0 && (
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    {capsOn(invite).map((cap: any) => (
                      <Tag key={cap.key} variant="code">
                        <span className={cap.color}>{cap.label}</span>
                      </Tag>
                    ))}
                  </div>
                )}
              </div>
            </ListRow>
          )}
        />
      </Reveal>
    </div>
  );
};

export default UserInvites;
